import {
  ScoringConfidence,
  ScoringResult,
  type ScoringChecklist,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FloatingPopover,
  MultiSelect,
  type MultiSelectOption,
  NumberedBadge,
  type OnChangeRequest,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaFilter } from 'react-icons/fa';

import { FormStack } from '../../components/forms/FormStack';
import type { ScoringResultsFilter } from '../../hooks/useScoringResultsFilter';

interface ScoringResultsFilterControlsProps {
  filter: ScoringResultsFilter;
  checklist: ScoringChecklist | null;
}

const ScoringSearchFilterControl = (props: { filter: ScoringResultsFilter }) => {
  const [value, setValue] = React.useState(props.filter.searchFilter);

  const onChange = React.useCallback(
    (value: string) => {
      setValue(value);
      props.filter.onSearchFilter(value);
    },
    [props.filter],
  );

  React.useEffect(() => {
    setValue(props.filter.searchFilter);
  }, [props.filter.searchFilter]);

  return (
    <ChakraV3.Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder='Search criteria...'
      size='sm'
    />
  );
};

const ScoringConfidenceFilterControl = (props: { filter: ScoringResultsFilter }) => {
  const options: MultiSelectOption<ScoringConfidence>[] = React.useMemo(
    () => [
      {
        value: ScoringConfidence.High,
        label: 'High',
        raw: ScoringConfidence.High,
        color: 'green',
      },
      {
        value: ScoringConfidence.Medium,
        label: 'Medium',
        raw: ScoringConfidence.Medium,
        color: 'yellow',
      },
      {
        value: ScoringConfidence.Low,
        label: 'Low',
        raw: ScoringConfidence.Low,
        color: 'red',
      },
    ],
    [],
  );

  const selectedValues: MultiSelectOption<ScoringConfidence>[] = React.useMemo(() => {
    return props.filter.confidenceFilter.flatMap((confidence) => {
      return options.find((o) => P.isEqual(o.raw, confidence)) ?? [];
    });
  }, [props.filter.confidenceFilter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<ScoringConfidence>) => {
      props.filter.onConfidenceFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All confidence levels'
      optionVariant='tag'
    />
  );
};

const ScoringResultFilterControl = (props: { filter: ScoringResultsFilter }) => {
  const options: MultiSelectOption<ScoringResult>[] = React.useMemo(
    () => [
      {
        value: ScoringResult.Passed,
        label: 'Passed',
        raw: ScoringResult.Passed,
        color: 'green',
      },
      {
        value: ScoringResult.Failed,
        label: 'Failed',
        raw: ScoringResult.Failed,
        color: 'red',
      },
    ],
    [],
  );

  const selectedValues: MultiSelectOption<ScoringResult>[] = React.useMemo(() => {
    return props.filter.resultFilter.flatMap((result) => {
      return options.find((o) => P.isEqual(o.raw, result)) ?? [];
    });
  }, [props.filter.resultFilter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<ScoringResult>) => {
      props.filter.onResultFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All results'
      optionVariant='tag'
    />
  );
};

const ScoringLabelFilterControl = (props: {
  filter: ScoringResultsFilter;
  checklist: ScoringChecklist | null;
}) => {
  const { filter, checklist } = props;

  const options: MultiSelectOption<string>[] = React.useMemo(() => {
    if (!checklist?.criteriaLabels) {
      return [];
    }

    return checklist.criteriaLabels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label.id,
        color: label.color,
      };
    });
  }, [checklist?.criteriaLabels]);

  const selectedValues: MultiSelectOption<string>[] = React.useMemo(() => {
    return filter.labelFilter.flatMap((labelId) => {
      return options.find((o) => P.isEqual(o.raw, labelId)) ?? [];
    });
  }, [filter.labelFilter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<string>) => {
      filter.onLabelFilter(req.values.map(({ raw }) => raw));
    },
    [filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

export const ScoringResultsFilterControls = (props: ScoringResultsFilterControlsProps) => {
  const { filter, checklist } = props;

  return (
    <FormStack orientation='horizontal' h='auto'>
      <ChakraV3.Stack maxW='2xs'>
        <ScoringSearchFilterControl filter={filter} />
      </ChakraV3.Stack>
      <FloatingPopover
        render={() => (
          <ChakraV3.Stack bg='white' gap={0} w='xs' shadow='2xl' rounded='md' overflow='hidden'>
            <ChakraV3.HStack py={2} px={4} borderBottomWidth='1px'>
              <ChakraV3.Text fontWeight='semibold'>Filters</ChakraV3.Text>
              <ChakraV3.Spacer />
              <NumberedBadge count={filter.activeFilterCount} />
            </ChakraV3.HStack>
            <FormStack py={4} px={4} orientation='vertical'>
              <ChakraV3.FormField label='Confidence'>
                <ScoringConfidenceFilterControl filter={filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Result'>
                <ScoringResultFilterControl filter={filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Labels'>
                <ScoringLabelFilterControl filter={filter} checklist={checklist} />
              </ChakraV3.FormField>

              <ChakraV3.Separator />

              <ChakraV3.Button
                w='fit-content'
                size='sm'
                variant='outline'
                disabled={filter.activeFilterCount === 0}
                onClick={filter.reset}
                colorPalette='brand'
              >
                Reset
              </ChakraV3.Button>
            </FormStack>
          </ChakraV3.Stack>
        )}
      >
        <ChakraV3.Button size='sm' variant='outline' lineHeight='unset'>
          <FaFilter />
          <ChakraV3.HStack>
            Filter
            <ChakraV3.Stack w='28px'>
              <NumberedBadge count={filter.activeFilterCount} />
            </ChakraV3.Stack>
          </ChakraV3.HStack>
          <FaCaretDown />
        </ChakraV3.Button>
      </FloatingPopover>
    </FormStack>
  );
};
